/**
 * Registry behind `useDismissGuard` (src/components/common/DismissGuard). Kept free of component
 * imports so Modal can consult it without an import cycle.
 */

interface GuardEntry {
  /** Set when the guarded input lives in a dialog that discards it on close. */
  modal?: object;
  /** Paths the draft survives navigating to, such as the pages of one multi-page editor. */
  allowNavigation?: (to: string) => boolean;
}

export const activeDismissGuards = new Map<string, GuardEntry>();

let requestConfirmation: (() => Promise<boolean>) | null = null;
let pendingConfirmation: Promise<boolean> | null = null;

export const setDismissConfirmation = (request: (() => Promise<boolean>) | null) => {
  requestConfirmation = request;
};

/**
 * Whether any surface currently holds unsaved changes. With a destination path, guards whose
 * draft survives navigating there don't count.
 */
export const hasActiveDismissGuards = (to?: string) =>
  to === undefined
    ? activeDismissGuards.size > 0
    : [...activeDismissGuards.values()].some((entry) => !entry.allowNavigation?.(to));

/** Whether a dirty surface lives in the given dialog. */
export const modalHasDismissGuards = (modal: object) =>
  [...activeDismissGuards.values()].some((entry) => entry.modal === modal);

/**
 * Asks once whether to discard unsaved changes. Resolves true at once when nothing is dirty.
 * Agreeing releases the discarded guards (all of them, or only `modal`'s), so follow-up steps of
 * the same action, such as the refresh after a project switch, don't ask again.
 */
export const confirmDismissGuards = async (options: { force?: boolean; modal?: object } = {}) => {
  const { force, modal } = options;
  if (!force && !hasActiveDismissGuards()) return true;
  // Simultaneous requests (e.g. a dialog close that also navigates) share one question.
  pendingConfirmation ??= (
    requestConfirmation
      ? requestConfirmation()
      : Promise.resolve(window.confirm("Discard unsaved changes?"))
  ).finally(() => {
    pendingConfirmation = null;
  });
  const discard = await pendingConfirmation;
  if (discard) {
    for (const [id, entry] of activeDismissGuards) {
      if (!modal || entry.modal === modal) activeDismissGuards.delete(id);
    }
  }
  return discard;
};
